import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Footer } from '../../components/Footer';
import { Shield, Upload, AlertCircle, CheckCircle, FileText, Image as ImageIcon, X } from 'lucide-react';
import { MessageModal } from '../../components/modals/MessageModal';
export function SubmitAdvocacyPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    project: '',
    developer: '',
    location: '',
    category: '',
    description: '',
    timeline: '',
    impactLevel: '',
    actionsTaken: '',
    desiredOutcome: ''
  });
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newFiles = Array.from(files).map(file => file.name);
      setUploadedFiles([...uploadedFiles, ...newFiles]);
    }
  };
  const removeFile = (index: number) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccessModalOpen(true);
    }, 2000);
  };
  const handleModalClose = () => {
    setIsSuccessModalOpen(false);
    navigate('/advocacy/my-posts');
  };
  return <div className="min-h-screen bg-[#f5f5f7] font-sans text-[#1f2933] flex flex-col" data-id="element-1857">
      {/* Header */}
      <header className="bg-white border-b border-gray-300 py-4 px-6 md:px-12" data-id="element-1858">
        <div className="max-w-4xl mx-auto flex justify-between items-center" data-id="element-1859">
          <Link to="/" className="flex items-center gap-2" data-id="element-1860">
            <div className="w-8 h-8 bg-[#1f2933] rounded-sm" data-id="element-1861"></div>
            <span className="font-bold text-xl tracking-tight" data-id="element-1862">
              Citiplots.com
            </span>
          </Link>
          <Link to="/advocacy" className="text-sm font-medium text-gray-600 hover:text-gray-900" data-id="element-1863">
            Cancel
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-6 py-12 w-full" data-id="element-1864">
        {/* Header */}
        <div className="mb-8" data-id="element-1865">
          <h1 className="text-4xl font-bold mb-4 flex items-center gap-3" data-id="element-1866">
            <Shield className="w-10 h-10 text-gray-400" data-id="element-1867" />
            Submit Advocacy Post
          </h1>
          <p className="text-lg text-gray-500 font-mono" data-id="element-1868">
            Share your experience to help other buyers and hold developers
            accountable
          </p>
        </div>

        {/* Verification Status */}
        <div className="bg-green-50 border border-green-200 p-6 mb-8 flex items-start gap-4" data-id="element-1869">
          <CheckCircle className="w-6 h-6 text-green-600 shrink-0" data-id="element-1870" />
          <div data-id="element-1871">
            <h3 className="font-bold text-green-900 mb-1" data-id="element-1872">
              Verification Status: Verified
            </h3>
            <p className="text-sm text-green-800" data-id="element-1873">
              Your buyer status has been verified. You can now submit advocacy
              posts.
            </p>
          </div>
        </div>

        {/* Important Guidelines */}
        <div className="bg-yellow-50 border border-yellow-200 p-6 mb-8" data-id="element-1874">
          <div className="flex items-start gap-4" data-id="element-1875">
            <AlertCircle className="w-6 h-6 text-yellow-600 shrink-0" data-id="element-1876" />
            <div data-id="element-1877">
              <h3 className="font-bold text-yellow-900 mb-3" data-id="element-1878">
                Important Guidelines
              </h3>
              <ul className="space-y-2 text-sm text-yellow-800" data-id="element-1879">
                <li className="flex items-start gap-2" data-id="element-1880">
                  <span className="shrink-0" data-id="element-1881">•</span>
                  <span data-id="element-1882">
                    All posts are reviewed by moderators before publication
                    (typically within 6 hours)
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-1883">
                  <span className="shrink-0" data-id="element-1884">•</span>
                  <span data-id="element-1885">
                    Provide factual, verifiable information with supporting
                    evidence
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-1886">
                  <span className="shrink-0" data-id="element-1887">•</span>
                  <span data-id="element-1888">
                    Avoid defamatory language; focus on facts and documented
                    issues
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-1889">
                  <span className="shrink-0" data-id="element-1890">•</span>
                  <span data-id="element-1891">Your post will be publicly visible once approved</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-gray-300 p-8 space-y-8" data-id="element-1892">
          {/* Basic Information */}
          <div data-id="element-1893">
            <h2 className="text-xl font-bold mb-6 pb-3 border-b border-gray-200" data-id="element-1894">
              Basic Information
            </h2>
            <div className="space-y-6" data-id="element-1895">
              <div data-id="element-1896">
                <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1897">
                  Issue Title *
                </label>
                <input type="text" required value={formData.title} onChange={e => setFormData({
                ...formData,
                title: e.target.value
              })} placeholder="e.g., Delayed Possession - 18 Months Overdue" className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-1898" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-1899">
                  Be specific and descriptive (50-100 characters recommended)
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1900">
                <div data-id="element-1901">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1902">
                    Project Name *
                  </label>
                  <input type="text" required value={formData.project} onChange={e => setFormData({
                  ...formData,
                  project: e.target.value
                })} placeholder="e.g., Green Valley Phase 2" className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-1903" />
                </div>

                <div data-id="element-1904">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1905">
                    Developer Name *
                  </label>
                  <input type="text" required value={formData.developer} onChange={e => setFormData({
                  ...formData,
                  developer: e.target.value
                })} placeholder="e.g., ABC Developers" className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-1906" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1907">
                <div data-id="element-1908">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1909">
                    Location *
                  </label>
                  <input type="text" required value={formData.location} onChange={e => setFormData({
                  ...formData,
                  location: e.target.value
                })} placeholder="e.g., Hinjewadi, Pune" className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-1910" />
                </div>

                <div data-id="element-1911">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1912">
                    Issue Category *
                  </label>
                  <select required value={formData.category} onChange={e => setFormData({
                  ...formData,
                  category: e.target.value
                })} className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-1913">
                    <option value="" data-id="element-1914">Select Category</option>
                    <option value="possession-delay" data-id="element-1915">Possession Delay</option>
                    <option value="infrastructure" data-id="element-1916">
                      Infrastructure Issues
                    </option>
                    <option value="documentation" data-id="element-1917">
                      Documentation Problems
                    </option>
                    <option value="financial" data-id="element-1918">Financial Disputes</option>
                    <option value="quality" data-id="element-1919">Construction Quality</option>
                    <option value="access" data-id="element-1920">Access Issues</option>
                    <option value="legal" data-id="element-1921">Legal Compliance</option>
                    <option value="other" data-id="element-1922">Other</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Description */}
          <div data-id="element-1923">
            <h2 className="text-xl font-bold mb-6 pb-3 border-b border-gray-200" data-id="element-1924">
              Issue Details
            </h2>
            <div className="space-y-6" data-id="element-1925">
              <div data-id="element-1926">
                <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1927">
                  Detailed Description *
                </label>
                <textarea required value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} placeholder="Describe the issue in detail. Include dates, communications, and specific problems you've faced..." rows={8} className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-1928" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-1929">
                  Minimum 200 characters. Be factual and specific.
                </p>
              </div>

              <div data-id="element-1930">
                <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1931">
                  Timeline of Events *
                </label>
                <textarea required value={formData.timeline} onChange={e => setFormData({
                ...formData,
                timeline: e.target.value
              })} placeholder="e.g., Booking: Jan 2020, Promised Possession: Dec 2021, Current Status: Still waiting as of Jan 2024..." rows={4} className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-1932" />
              </div>

              <div data-id="element-1933">
                <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1934">
                  Impact Level *
                </label>
                <select required value={formData.impactLevel} onChange={e => setFormData({
                ...formData,
                impactLevel: e.target.value
              })} className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-1935">
                  <option value="" data-id="element-1936">Select Impact Level</option>
                  <option value="high" data-id="element-1937">
                    High - Severe financial/personal impact
                  </option>
                  <option value="medium" data-id="element-1938">
                    Medium - Significant inconvenience
                  </option>
                  <option value="low" data-id="element-1939">Low - Minor issue</option>
                </select>
              </div>

              <div data-id="element-1940">
                <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1941">
                  Actions Taken So Far
                </label>
                <textarea value={formData.actionsTaken} onChange={e => setFormData({
                ...formData,
                actionsTaken: e.target.value
              })} placeholder="e.g., Sent multiple emails, filed complaint with RERA, consulted lawyer..." rows={4} className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-1942" />
              </div>

              <div data-id="element-1943">
                <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1944">
                  Desired Outcome
                </label>
                <textarea value={formData.desiredOutcome} onChange={e => setFormData({
                ...formData,
                desiredOutcome: e.target.value
              })} placeholder="What resolution are you seeking? e.g., Immediate possession, compensation, refund..." rows={3} className="w-full p-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-1945" />
              </div>
            </div>
          </div>

          {/* Supporting Evidence */}
          <div data-id="element-1946">
            <h2 className="text-xl font-bold mb-6 pb-3 border-b border-gray-200" data-id="element-1947">
              Supporting Evidence
            </h2>
            <div className="space-y-6" data-id="element-1948">
              <div data-id="element-1949">
                <label className="block text-sm font-bold text-gray-700 mb-3" data-id="element-1950">
                  Upload Documents/Images
                </label>
                <div className="border-2 border-dashed border-gray-300 p-8 bg-gray-50 text-center" data-id="element-1951">
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-3" data-id="element-1952" />
                  <p className="text-sm font-bold mb-2" data-id="element-1953">
                    Drag & drop files or click to browse
                  </p>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-1954">
                    Supported: PDF, JPG, PNG (Max 10MB per file, up to 10 files)
                  </p>
                  <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} className="hidden" id="file-upload" data-id="element-1955" />
                  <label htmlFor="file-upload" className="inline-block px-6 py-2 bg-white border border-gray-300 text-sm font-bold cursor-pointer hover:bg-gray-50" data-id="element-1956">
                    Choose Files
                  </label>
                </div>

                {uploadedFiles.length > 0 && <div className="mt-4 space-y-2" data-id="element-1957">
                    {uploadedFiles.map((file, index) => <div key={index} className="flex items-center justify-between p-3 bg-white border border-gray-200" data-id="element-1958">
                        <div className="flex items-center gap-3" data-id="element-1959">
                          <FileText className="w-5 h-5 text-gray-400" data-id="element-1960" />
                          <span className="text-sm" data-id="element-1961">{file}</span>
                        </div>
                        <button type="button" onClick={() => removeFile(index)} className="text-red-600 hover:text-red-800" data-id="element-1962">
                          <X className="w-5 h-5" data-id="element-1963" />
                        </button>
                      </div>)}
                  </div>}

                <p className="text-xs text-gray-500 mt-3" data-id="element-1964">
                  Recommended: Booking receipts, emails, legal notices, photos
                  of issues
                </p>
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="bg-gray-50 border border-gray-200 p-6" data-id="element-1965">
            <label className="flex items-start gap-3 cursor-pointer" data-id="element-1966">
              <input type="checkbox" required className="mt-1 accent-[#1f2933]" data-id="element-1967" />
              <span className="text-sm text-gray-700" data-id="element-1968">
                I confirm that the information provided is accurate and factual
                to the best of my knowledge. I understand that false or
                defamatory content may result in removal and account suspension.
                I agree to the{' '}
                <Link to="/terms" className="text-blue-600 hover:underline" data-id="element-1969">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/advocacy/guidelines" className="text-blue-600 hover:underline" data-id="element-1970">
                  Advocacy Guidelines
                </Link>
                .
              </span>
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200" data-id="element-1971">
            <Link to="/advocacy" className="px-6 py-3 border border-gray-300 text-sm font-bold hover:bg-gray-50" data-id="element-1972">
              Cancel
            </Link>
            <button type="submit" disabled={isSubmitting} className="px-8 py-3 bg-[#1f2933] text-white text-sm font-bold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1973">
              {isSubmitting ? 'Submitting...' : 'Submit for Review'}
            </button>
          </div>
        </form>
      </main>

      <Footer data-id="element-1974" />

      <MessageModal isOpen={isSuccessModalOpen} onClose={handleModalClose} title="Post Submitted Successfully" message="Your advocacy post has been submitted for moderation review. You'll be notified via email once it's approved (typically within 6 hours). Thank you for contributing to buyer transparency." type="success" data-id="element-1975" />
    </div>;
}